import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import { ShoppingCart, Menu, X, ChevronDown } from 'lucide-react';
import { sfxClick, sfxHover, sfxPowerUp, startHum } from '@/lib/soundFX';
import OnyxGlassPanel from '@/components/OnyxGlassPanel';
import NeuralDeploymentGrid from '@/components/NeuralDeploymentGrid';
const LOGO_URL = '/manus-storage/realai-logo-new_7035b804.png';

interface CyberHeaderProps {
  onOpenCart: () => void;
  cartCount: number;
  onScrollToSection: (section: string) => void;
  onSetDept?: (deptId: string) => void;
  agentNames?: string[];
  onDeployClick?: () => void;
}

const DEPT_PILLS = [
  { label: 'EXEC', id: 'enterprise', color: '#7CFC00' },
  { label: 'SALES', id: 'sales', color: '#00FF88' },
  { label: 'MKTG', id: 'marketing', color: '#FF0055' },
  { label: 'TECH', id: 'technology', color: '#00FFFF' },
  { label: 'FIN', id: 'finance', color: '#FFB800' },
  { label: 'OPS', id: 'operations', color: '#00A6EB' },
  { label: 'CX', id: 'customer-service', color: '#FF6B00' },
  { label: 'SMB', id: 'small-business', color: '#A855F7' },
  { label: 'HOSP', id: 'hospitality', color: '#FF1493' },
  { label: 'ECOM', id: 'ecommerce', color: '#FF8C00' },
];

const DOWNLOAD_LINKS = [
  { label: 'AI PLAYBOOKS', path: '/playbooks', color: '#00A6EB' },
  { label: 'PROMPT PACKS', path: '/prompt-packs', color: '#A855F7' },
  { label: 'AUTOMATION', path: '/automation-templates', color: '#00FF88' },
  { label: 'AI TRAINING', path: '/ai-training', color: '#FFB800' },
];
const MY_DOWNLOADS_LINK = { label: '⬡ MY DOWNLOADS', path: '/my-downloads', color: '#00FF88' };

// ── TOP BAR HEIGHT (px) ────────────────────────────────────────────────────
export const TOP_BAR_H = 60;
// ── GLASS PANEL HEIGHT (px) — desktop only ─────────────────────────────────
export const GLASS_PANEL_H = 380;
// ── TOTAL HEADER HEIGHT ────────────────────────────────────────────────────
export const HEADER_TOTAL_H = TOP_BAR_H + GLASS_PANEL_H;

export function CyberHeader({ onOpenCart, cartCount, onScrollToSection, onSetDept, onDeployClick }: CyberHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [deptOpen, setDeptOpen] = useState(false);
  const [downloadsOpen, setDownloadsOpen] = useState(false);
  const [location, navigate] = useLocation();
  const [liveAgents] = useState(232);
  const [activeDeploys] = useState(47);
  const deptRef = useRef<HTMLDivElement>(null);
  const downloadsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', onScroll, { passive: true });
    const onFirstClick = () => { startHum(); window.removeEventListener('click', onFirstClick); };
    window.addEventListener('click', onFirstClick);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('click', onFirstClick);
    };
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (deptRef.current && !deptRef.current.contains(e.target as Node)) setDeptOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (downloadsRef.current && !downloadsRef.current.contains(e.target as Node)) setDownloadsOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <>
      <style>{`
        @keyframes navPulse { 0%,100%{opacity:.5} 50%{opacity:1} }
        @keyframes cartBounce { 0%,100%{transform:scale(1)} 30%{transform:scale(1.2)} 60%{transform:scale(.95)} }
        @keyframes topBarScan { from{left:-100%} to{left:200%} }
        @keyframes glassShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes cornerPulseH { 0%,100%{opacity:.6;box-shadow:0 0 6px rgba(0,166,235,0.4)} 50%{opacity:1;box-shadow:0 0 14px rgba(0,166,235,0.8)} }
        @keyframes chromaticScan { 0%{left:-100%;opacity:0} 10%{opacity:1} 90%{opacity:1} 100%{left:200%;opacity:0} }
        @keyframes panelGlow { 0%,100%{box-shadow:0 0 20px rgba(0,166,235,0.04)} 50%{box-shadow:0 0 40px rgba(0,166,235,0.1)} }
        @keyframes glassTileShimmer { 0%{opacity:0.4} 50%{opacity:0.7} 100%{opacity:0.4} }
        @keyframes scanLine { 0%{top:-2px;opacity:0} 5%{opacity:1} 95%{opacity:0.6} 100%{top:100%;opacity:0} }
        @keyframes dataFlicker { 0%,100%{opacity:0.15} 50%{opacity:0.35} }
        @keyframes pulseRing { 0%,100%{transform:scale(1);opacity:0.5} 50%{transform:scale(1.08);opacity:1} }

        /* ── TOP BAR LINKS ── */
        .ch-nav-link {
          font-family: Orbitron, monospace;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.14em;
          color: rgba(232,244,255,0.65);
          padding: 0.32rem 0.65rem;
          border-radius: 2px;
          border: 1px solid transparent;
          background: none;
          cursor: pointer;
          white-space: nowrap;
          position: relative;
          overflow: hidden;
          transition: color 0.18s, background 0.18s, border-color 0.18s, text-shadow 0.18s;
        }
        .ch-nav-link:hover {
          color: #00A6EB;
          text-shadow: 0 0 12px rgba(0,166,235,0.8);
          background: rgba(0,166,235,0.08);
          border-color: rgba(0,166,235,0.3);
        }
        .ch-nav-link::after {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 60%; height: 100%;
          background: linear-gradient(90deg, transparent, rgba(0,166,235,0.15), transparent);
          transition: left 0.4s ease;
        }
        .ch-nav-link:hover::after { left: 150%; }

        /* ── DEPT PILLS ── */
        .ch-dept-pill {
          font-family: Share Tech Mono, monospace;
          font-size: 0.62rem;
          letter-spacing: 0.08em;
          padding: 0.22rem 0.5rem;
          border: 1px solid rgba(0,166,235,0.2);
          border-radius: 2px;
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
          background: transparent;
          text-align: center;
        }

        /* ── DEPLOY BUTTON ── */
        .ch-deploy-btn {
          font-family: Orbitron, monospace;
          font-size: 0.7rem;
          font-weight: 800;
          letter-spacing: 0.14em;
          color: #FF0055;
          padding: 0.4rem 1.1rem;
          border: 1px solid rgba(255,0,85,0.55);
          border-radius: 2px;
          background: rgba(255,0,85,0.08);
          cursor: pointer;
          transition: all 0.18s;
          text-shadow: 0 0 10px rgba(255,0,85,0.6);
          white-space: nowrap;
          flex-shrink: 0;
          position: relative;
          overflow: hidden;
        }
        .ch-deploy-btn:hover {
          background: rgba(255,0,85,0.2);
          border-color: rgba(255,0,85,0.9);
          box-shadow: 0 0 24px rgba(255,0,85,0.35), inset 0 0 20px rgba(255,0,85,0.08);
        }
        .ch-deploy-btn:active { transform: scale(0.97); }

        /* ── CART BUTTON ── */
        .ch-cart-btn {
          position: relative;
          display: flex;
          align-items: center;
          gap: 0.4rem;
          font-family: Orbitron, monospace;
          font-size: 0.66rem;
          font-weight: 700;
          letter-spacing: 0.1em;
          color: rgba(0,166,235,0.9);
          padding: 0.4rem 0.85rem;
          border: 1px solid rgba(0,166,235,0.35);
          border-radius: 2px;
          background: rgba(0,166,235,0.07);
          cursor: pointer;
          transition: all 0.18s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .ch-cart-btn:hover {
          background: rgba(0,166,235,0.16);
          border-color: rgba(0,166,235,0.7);
          box-shadow: 0 0 18px rgba(0,166,235,0.3);
        }
        .ch-cart-btn:active { transform: scale(0.97); }

        /* ── GLASS PANEL ── */
        .ch-glass-panel {
          width: 100%;
          background: linear-gradient(180deg,
            rgba(3,6,18,0.98) 0%,
            rgba(5,10,24,0.96) 40%,
            rgba(6,12,28,0.95) 70%,
            rgba(4,8,20,0.99) 100%
          );
          border-bottom: 1px solid rgba(0,166,235,0.25);
          box-shadow:
            0 8px 40px rgba(0,0,0,0.7),
            0 0 120px rgba(0,166,235,0.06),
            0 0 200px rgba(255,0,85,0.02),
            inset 0 -1px 0 rgba(0,166,235,0.15),
            inset 0 1px 0 rgba(255,255,255,0.03);
          backdrop-filter: blur(32px) saturate(160%);
          position: relative;
          overflow: hidden;
          animation: panelGlow 4s ease-in-out infinite;
        }
        .ch-glass-panel::before {
          content: '';
          position: absolute;
          inset: 0;
          background-image:
            repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,166,235,0.012) 3px, rgba(0,166,235,0.012) 4px),
            repeating-linear-gradient(90deg, transparent, transparent 3px, rgba(0,166,235,0.008) 3px, rgba(0,166,235,0.008) 4px);
          pointer-events: none;
          z-index: 0;
        }
        .ch-glass-panel::after {
          content: '';
          position: absolute;
          top: 0; left: -100%; width: 60%; height: 1px;
          background: linear-gradient(90deg, transparent 0%, rgba(0,166,235,0.8) 20%, rgba(168,85,247,0.6) 50%, rgba(255,0,85,0.8) 80%, transparent 100%);
          z-index: 3;
          animation: chromaticScan 6s ease-in-out infinite;
        }

        /* ── DROPDOWN ── */
        .ch-dropdown {
          position: absolute;
          top: calc(100% + 8px);
          left: 50%;
          transform: translateX(-50%);
          background: rgba(4,6,14,0.99);
          border: 1px solid rgba(0,166,235,0.3);
          border-radius: 4px;
          padding: 0.75rem;
          z-index: 300;
          box-shadow: 0 20px 60px rgba(0,0,0,0.85), 0 0 40px rgba(0,166,235,0.08);
          backdrop-filter: blur(24px);
          animation: dropIn 0.18s cubic-bezier(0.23,1,0.32,1);
        }
        @keyframes dropIn {
          from { opacity: 0; transform: translateX(-50%) translateY(-8px) scale(0.97); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0) scale(1); }
        }

        /* ── RESPONSIVE ── */
        @media (max-width: 1023px) {
          .ch-desktop { display: none !important; }
          .ch-mobile-btn { display: flex !important; }
          .ch-glass-panel { display: none !important; }
          .ch-mobile-neural { display: block !important; }
        }
        @media (max-width: 400px) {
          .ch-topbar-inner { padding: 0 0.6rem !important; gap: 0.35rem !important; }
          .ch-logo-text-main { font-size: 0.78rem !important; }
          .ch-logo-text-sub { font-size: 0.48rem !important; }
          .ch-logo-img { height: 30px !important; width: 30px !important; }
          .ch-cart-btn { padding: 0.35rem 0.6rem !important; }
          .ch-mobile-btn { width: 34px !important; height: 34px !important; }
        }
        @media (min-width: 1024px) {
          .ch-mobile-btn { display: none !important; }
          .ch-mobile-neural { display: none !important; }
        }
      `}</style>

      {/* ════════════════════════════════════════════════════════
          ZONE 1 — SLIM TOP BAR (fixed, always visible)
      ════════════════════════════════════════════════════════ */}
      <header className="ch-topbar" style={{
        position: 'fixed',
        top: 0, left: 0, right: 0,
        zIndex: 150,
        height: TOP_BAR_H,
        background: scrolled
          ? 'rgba(4,6,14,0.99)'
          : 'rgba(4,6,14,0.94)',
        backdropFilter: 'blur(28px) saturate(180%)',
        borderBottom: `1px solid ${scrolled ? 'rgba(0,166,235,0.45)' : 'rgba(0,166,235,0.15)'}`,
        boxShadow: scrolled
          ? '0 4px 40px rgba(0,0,0,0.7), 0 0 60px rgba(0,166,235,0.05)'
          : 'none',
        transition: 'all 0.3s cubic-bezier(0.23,1,0.32,1)',
      }}>
        {/* Top accent line */}
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          background: 'linear-gradient(90deg, transparent 0%, #00A6EB 25%, #FF0055 55%, #A855F7 75%, transparent 100%)',
          opacity: 0.95,
        }} />
        {/* Scan sweep shimmer */}
        <div style={{
          position: 'absolute', top: 0, left: 0, bottom: 0, width: '30%',
          background: 'linear-gradient(90deg, transparent, rgba(0,166,235,0.04), transparent)',
          animation: 'topBarScan 6s linear infinite',
          pointerEvents: 'none',
        }} />

        <div className="ch-topbar-inner" style={{
          maxWidth: 1700, margin: '0 auto', padding: '0 1.5rem',
          height: '100%', display: 'flex', alignItems: 'center', gap: '0.6rem',
        }}>
          {/* ── Logo ── */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', flexShrink: 0 }}
            onClick={() => { sfxClick(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
          >
            <div style={{ position: 'relative' }}>
              <img src={LOGO_URL} alt="RealAI" className="ch-logo-img" style={{
                height: 36, width: 36, borderRadius: '50%', objectFit: 'cover',
                border: '1.5px solid rgba(0,166,235,0.5)',
                boxShadow: '0 0 16px rgba(0,166,235,0.35)',
              }} onError={e => { (e.target as HTMLImageElement).style.display='none'; }} />
              <div style={{
                position: 'absolute', inset: -2, borderRadius: '50%',
                border: '1px solid rgba(0,166,235,0.2)',
                animation: 'cornerPulseH 3s ease-in-out infinite',
              }} />
            </div>
            <div style={{ lineHeight: 1.1 }}>
              <div className="ch-logo-text-main" style={{ fontFamily: 'Orbitron, monospace', fontWeight: 900, fontSize: '0.88rem', letterSpacing: '0.1em', color: '#00A6EB', textShadow: '0 0 14px rgba(0,166,235,0.6)' }}>
                REAL<span style={{ color: '#FF0055', textShadow: '0 0 14px rgba(255,0,85,0.6)' }}>AI</span>
              </div>
              <div className="ch-logo-text-sub" style={{ fontFamily: 'Orbitron, monospace', fontWeight: 700, fontSize: '0.55rem', letterSpacing: '0.24em', color: 'rgba(232,244,255,0.45)' }}>EMPLOYEES</div>
            </div>
          </div>

          {/* ── Vertical divider ── */}
          <div className="ch-desktop" style={{ width: 1, height: 28, background: 'rgba(0,166,235,0.2)', flexShrink: 0 }} />

          {/* ── Desktop nav ── */}
          <nav className="ch-desktop" style={{ display: 'flex', alignItems: 'center', gap: '0.05rem' }}>
            {/* Primary scroll links */}
            {[
              { label: 'WORKFORCE', section: 'workforce' },
              { label: 'ABOUT', section: 'about' },
              { label: 'PRICING', section: 'pricing' },
              { label: 'BUNDLES', section: 'bundles' },
            ].map(item => (
              <button key={item.label} className="ch-nav-link"
                onClick={() => { sfxClick(); onScrollToSection(item.section); }}>
                {item.label}
              </button>
            ))}

            {/* Departments dropdown */}
            <div ref={deptRef} style={{ position: 'relative' }}>
              <button className="ch-nav-link"
                style={{ display: 'flex', alignItems: 'center', gap: '0.28rem' }}
                onClick={() => { sfxClick(); setDeptOpen(o => !o); setDownloadsOpen(false); }}>
                DEPARTMENTS
                <ChevronDown size={10} style={{ transition: 'transform 0.2s', transform: deptOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {deptOpen && (
                <div className="ch-dropdown" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '0.4rem', minWidth: 340 }}>
                  {DEPT_PILLS.map(d => (
                    <button key={d.id} className="ch-dept-pill"
                      style={{ color: d.color, borderColor: `${d.color}30` }}
                      onMouseEnter={() => sfxHover()}
                      onClick={() => { sfxClick(); setDeptOpen(false); if (location === '/') { if (onSetDept) onSetDept(d.id); onScrollToSection('departments'); } else { navigate(`/departments?dept=${d.id}`); } }}>
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Downloads dropdown */}
            <div ref={downloadsRef} style={{ position: 'relative' }}>
              <button className="ch-nav-link"
                style={{ display: 'flex', alignItems: 'center', gap: '0.28rem', color: 'rgba(0,255,136,0.75)' }}
                onClick={() => { sfxClick(); setDownloadsOpen(o => !o); setDeptOpen(false); }}>
                DOWNLOADS
                <ChevronDown size={10} style={{ transition: 'transform 0.2s', transform: downloadsOpen ? 'rotate(180deg)' : 'none' }} />
              </button>
              {downloadsOpen && (
                <div className="ch-dropdown" style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', minWidth: 190, borderColor: 'rgba(0,255,136,0.25)' }}>
                  {/* MY DOWNLOADS — pinned top item */}
                  <button
                    onClick={() => { sfxClick(); setDownloadsOpen(false); navigate(MY_DOWNLOADS_LINK.path); }}
                    style={{
                      fontFamily: 'Orbitron, monospace', fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.1em',
                      color: '#00FF88', background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.4)',
                      borderRadius: 2, padding: '0.5rem 0.7rem', cursor: 'pointer', textAlign: 'left',
                      transition: 'all 0.15s', marginBottom: '0.2rem',
                    }}
                    onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.2)'; (e.currentTarget as HTMLElement).style.boxShadow = '0 0 12px rgba(0,255,136,0.2)'; }}
                    onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(0,255,136,0.1)'; (e.currentTarget as HTMLElement).style.boxShadow = 'none'; }}
                  >
                    ⬡ MY DOWNLOADS
                  </button>
                  <div style={{ height: 1, background: 'rgba(0,166,235,0.15)', margin: '0.1rem 0 0.3rem' }} />
                  {DOWNLOAD_LINKS.map(d => (
                    <button key={d.path}
                      onClick={() => { sfxClick(); setDownloadsOpen(false); navigate(d.path); }}
                      style={{
                        fontFamily: 'Share Tech Mono, monospace', fontSize: '0.66rem', letterSpacing: '0.1em',
                        color: d.color, background: `${d.color}08`, border: `1px solid ${d.color}25`,
                        borderRadius: 2, padding: '0.42rem 0.7rem', cursor: 'pointer', textAlign: 'left',
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = `${d.color}18`; (e.currentTarget as HTMLElement).style.borderColor = `${d.color}60`; }}
                      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = `${d.color}08`; (e.currentTarget as HTMLElement).style.borderColor = `${d.color}25`; }}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* ── Spacer ── */}
          <div style={{ flex: 1 }} />

          {/* ── Live indicator ── */}
          <div className="ch-desktop" style={{ display: 'flex', alignItems: 'center', gap: '0.38rem', flexShrink: 0 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#00FF88', boxShadow: '0 0 8px #00FF88', animation: 'navPulse 1.5s ease-in-out infinite' }} />
            <span style={{ fontFamily: 'Share Tech Mono, monospace', fontSize: '0.62rem', color: 'rgba(0,255,136,0.85)', letterSpacing: '0.15em' }}>232 ONLINE</span>
          </div>

          {/* ── DEPLOY NOW ── */}
          <button className="ch-deploy-btn ch-desktop" aria-label="Deploy AI workforce"
            onClick={() => { sfxPowerUp(); onDeployClick ? onDeployClick() : onScrollToSection('workforce'); }}>
            ⚡ DEPLOY NOW
          </button>

          {/* ── DATA CORE cart ── */}
          <button className="ch-cart-btn" aria-label="Open cart" onClick={() => { sfxClick(); onOpenCart(); }}>
            <ShoppingCart size={14} />
            <span className="ch-desktop">DATA CORE</span>
            {cartCount > 0 && (
              <span style={{
                position: 'absolute', top: -7, right: -7,
                width: 18, height: 18, borderRadius: '50%',
                background: '#FF0055', color: '#fff',
                fontSize: '0.6rem', fontWeight: 700,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 0 10px rgba(255,0,85,0.7)',
                animation: 'cartBounce 0.4s ease-out',
              }}>{cartCount}</span>
            )}
          </button>

          {/* ── Mobile hamburger ── */}
          <button
            className="ch-mobile-btn"
            style={{ display: 'none', alignItems: 'center', justifyContent: 'center', width: 38, height: 38, background: 'rgba(0,166,235,0.08)', border: '1px solid rgba(0,166,235,0.25)', borderRadius: 2, color: '#00A6EB', cursor: 'pointer', flexShrink: 0 }}
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            onClick={() => { sfxClick(); setMobileOpen(o => !o); }}
          >
            {mobileOpen ? <X size={17} /> : <Menu size={17} />}
          </button>
        </div>
      </header>

      {/* ════════════════════════════════════════════════════════
          ZONE 2 — GLASS COMMAND PANEL (fixed, below top bar, desktop only)
      ════════════════════════════════════════════════════════ */}
      <div className="ch-glass-panel" style={{
        position: 'fixed',
        top: TOP_BAR_H,
        left: 0, right: 0,
        zIndex: 140,
        height: GLASS_PANEL_H,
      }}>
        {/* Corner brackets */}
        {[
          { top: 6, left: 6, borderTop: '1.5px solid rgba(0,166,235,0.6)', borderLeft: '1.5px solid rgba(0,166,235,0.6)' },
          { top: 6, right: 6, borderTop: '1.5px solid rgba(0,166,235,0.6)', borderRight: '1.5px solid rgba(0,166,235,0.6)' },
          { bottom: 6, left: 6, borderBottom: '1.5px solid rgba(0,166,235,0.6)', borderLeft: '1.5px solid rgba(0,166,235,0.6)' },
          { bottom: 6, right: 6, borderBottom: '1.5px solid rgba(0,166,235,0.6)', borderRight: '1.5px solid rgba(0,166,235,0.6)' },
        ].map((s, i) => (
          <div key={i} style={{ position: 'absolute', width: 14, height: 14, zIndex: 2, animation: 'cornerPulseH 3s ease-in-out infinite', ...s }} />
        ))}

        <div style={{
          maxWidth: '100%', margin: '0', padding: '0.5rem 1.5rem',
          height: '100%', position: 'relative', zIndex: 1,
        }}>
          <OnyxGlassPanel />
        </div>
        {/* Bottom fade into page */}
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0, height: 24,
          background: 'linear-gradient(to bottom, transparent, rgba(4,8,20,0.4))',
          pointerEvents: 'none', zIndex: 2,
        }} />
      </div>

      {/* ════ MOBILE ZONE 2 — NEURAL DEPLOYMENT GRID ════ */}
      <div className="ch-mobile-neural" style={{ position: 'relative', width: '100%', zIndex: 1, display: 'none' }}>
        <NeuralDeploymentGrid />
      </div>

      {/* ════════════════════════════════════════════════════════
          MOBILE FULL-SCREEN MENU
      ════════════════════════════════════════════════════════ */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 200,
          background: 'rgba(4,6,14,0.99)',
          backdropFilter: 'blur(24px)',
          display: 'flex', flexDirection: 'column',
          paddingTop: TOP_BAR_H, overflowY: 'auto',
        }}>
          <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {[
              { label: 'WORKFORCE', section: 'workforce' },
              { label: 'ABOUT', section: 'about' },
              { label: 'PRICING', section: 'pricing' },
              { label: 'BUNDLES', section: 'bundles' },
            ].map(item => (
              <button key={item.label} onClick={() => { sfxClick(); onScrollToSection(item.section); setMobileOpen(false); }} style={{
                fontFamily: 'Orbitron, monospace', fontSize: '1rem', fontWeight: 700, letterSpacing: '0.12em',
                color: 'rgba(232,244,255,0.85)', padding: '1rem', textAlign: 'left',
                background: 'none', border: 'none', borderBottom: '1px solid rgba(0,166,235,0.1)', cursor: 'pointer',
              }}>{item.label}</button>
            ))}
            <div style={{ padding: '1rem 0 0.5rem', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem', color: 'rgba(0,166,235,0.6)', letterSpacing: '0.2em' }}>DEPARTMENTS</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '0.45rem', marginBottom: '1rem' }}>
              {DEPT_PILLS.map(d => (
                <button key={d.id} className="ch-dept-pill"
                  style={{ fontSize: '0.76rem', padding: '0.6rem 0.7rem', color: d.color, borderColor: `${d.color}30` }}
                  onClick={() => { sfxClick(); setMobileOpen(false); if (location === '/') { if (onSetDept) onSetDept(d.id); onScrollToSection('departments'); } else { navigate(`/departments?dept=${d.id}`); } }}>
                  {d.label}
                </button>
              ))}
            </div>
            <div style={{ padding: '0.75rem 0 0.5rem', fontFamily: 'Share Tech Mono, monospace', fontSize: '0.68rem', color: 'rgba(0,255,136,0.6)', letterSpacing: '0.2em' }}>DOWNLOADS</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.3rem', marginBottom: '1rem' }}>
              <button onClick={() => { sfxClick(); setMobileOpen(false); navigate('/my-downloads'); }} style={{
                fontFamily: 'Orbitron, monospace', fontSize: '0.8rem', fontWeight: 700, letterSpacing: '0.08em',
                color: '#00FF88', background: 'rgba(0,255,136,0.1)', border: '1px solid rgba(0,255,136,0.4)',
                borderRadius: 2, padding: '0.7rem 0.7rem', cursor: 'pointer', textAlign: 'left', marginBottom: '0.2rem',
              }}>
                ⬡ MY DOWNLOADS
              </button>
              {DOWNLOAD_LINKS.map(d => (
                <button key={d.path} onClick={() => { sfxClick(); setMobileOpen(false); navigate(d.path); }} style={{
                  fontFamily: 'Share Tech Mono, monospace', fontSize: '0.8rem', letterSpacing: '0.08em',
                  color: d.color, background: `${d.color}08`, border: `1px solid ${d.color}25`,
                  borderRadius: 2, padding: '0.6rem 0.7rem', cursor: 'pointer', textAlign: 'left',
                }}>
                  {d.label}
                </button>
              ))}
            </div>
            <button className="ch-deploy-btn" style={{ fontSize: '0.88rem', padding: '0.9rem', marginBottom: '0.5rem' }}
              onClick={() => { sfxPowerUp(); setMobileOpen(false); onDeployClick ? onDeployClick() : onScrollToSection('workforce'); }}>
              ⚡ DEPLOY NOW
            </button>
            <button className="ch-cart-btn" style={{ justifyContent: 'center', fontSize: '0.88rem', padding: '0.9rem' }}
              onClick={() => { sfxClick(); setMobileOpen(false); onOpenCart(); }}>
              <ShoppingCart size={16} /> DATA CORE {cartCount > 0 ? `(${cartCount})` : ''}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default CyberHeader;
